import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CONTACT_EMAIL_MAX_LIMIT,
  CONTACT_EMAIL_PAGE_SIZE,
  ContactEmailItem,
  ContactEmailStatus,
  contactEmailErrorMessage,
  getContactEmailStatus,
  getContactEmails,
  isEmailForbidden,
} from '../../services/ContactEmailService';

/** Atualiza a linha do tempo de e-mail enquanto a conversa está aberta (a caixa é lida a cada 2 min no servidor). */
export const CONTACT_EMAIL_REFRESH_MS = 60_000;

/** Mensagem quando a linha do tempo de e-mail não carrega (o servidor não mandou uma própria). */
export const CONTACT_EMAIL_LOAD_ERROR = 'Não foi possível carregar os e-mails deste cliente. Tente de novo em instantes.';

export interface ContactEmailsState {
  emails: ContactEmailItem[];
  status: ContactEmailStatus | null;
  loading: boolean;
  /** Usuário sem o módulo de e-mail: nada de e-mail na conversa. */
  forbidden: boolean;
  /** Falha ao carregar (não 403), já em português; null = carregou. A conversa não fica vazia em silêncio. */
  error: string | null;
  /** Há e-mails mais antigos que os carregados. */
  hasMore: boolean;
  /** Total de e-mails do cliente no servidor. */
  total: number;
  /** Quantos e-mails mais recentes a conversa pede agora. */
  limit: number;
  /** Ainda dá para pedir mais (o servidor devolve no máximo {@link CONTACT_EMAIL_MAX_LIMIT}). */
  canLoadMore: boolean;
  loadingMore: boolean;
  /** Carrega mais {@link CONTACT_EMAIL_PAGE_SIZE} e-mails anteriores. */
  loadMore: () => Promise<void>;
  reload: () => Promise<void>;
  /** Mostra na hora a resposta recém-enviada (antes do próximo recarregamento). */
  append: (item: ContactEmailItem) => void;
}

export function useContactEmails(sectorId: number | null | undefined, contactId: number | null | undefined, enabled = true): ContactEmailsState {
  const [emails, setEmails] = useState<ContactEmailItem[]>([]);
  const [status, setStatus] = useState<ContactEmailStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(CONTACT_EMAIL_PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  /** Conversa cujo pedido ainda vale; '' = nenhuma (ex.: grupo aberto). Resposta de outra conversa é descartada. */
  const current = useRef<string>('');
  const limitRef = useRef(CONTACT_EMAIL_PAGE_SIZE);

  const active = enabled && !!sectorId && !!contactId;

  const load = useCallback(async (silent: boolean, wanted?: number) => {
    if (!enabled || !sectorId || !contactId) {
      current.current = '';
      return;
    }
    const key = `${sectorId}:${contactId}`;
    current.current = key;
    const size = wanted ?? limitRef.current;
    if (!silent) setLoading(true);
    try {
      const [list, st] = await Promise.all([getContactEmails(sectorId, contactId, size), getContactEmailStatus(sectorId, contactId)]);
      if (current.current !== key) return;
      setEmails(list.items || []);
      setHasMore(!!list.hasMore);
      setTotal(list.total ?? (list.items || []).length);
      setStatus(st);
      setForbidden(false);
      setError(null);
      limitRef.current = size;
      setLimit(size);
    } catch (err) {
      if (current.current !== key) return;
      if (isEmailForbidden(err)) {
        setForbidden(true);
        setError(null);
      } else {
        setError(contactEmailErrorMessage(err, CONTACT_EMAIL_LOAD_ERROR));
      }
      if (!silent) setEmails([]);
    } finally {
      if (current.current === key && !silent) setLoading(false);
    }
  }, [enabled, sectorId, contactId]);

  useEffect(() => {
    // Troca de conversa (ou grupo/sem cliente): nada da conversa anterior pode aparecer aqui.
    current.current = '';
    limitRef.current = CONTACT_EMAIL_PAGE_SIZE;
    setLimit(CONTACT_EMAIL_PAGE_SIZE);
    setEmails([]);
    setStatus(null);
    setError(null);
    setHasMore(false);
    setTotal(0);
    setLoading(false);
    setLoadingMore(false);
    if (!active) return undefined;
    void load(false);
    const timer = window.setInterval(() => { void load(true); }, CONTACT_EMAIL_REFRESH_MS);
    return () => {
      window.clearInterval(timer);
      current.current = '';
    };
  }, [load, active]);

  const append = useCallback((item: ContactEmailItem) => {
    setEmails((prev) => (prev.some((e) => e.id === item.id) ? prev : [...prev, item]));
  }, []);

  const reload = useCallback(() => load(true), [load]);

  const canLoadMore = hasMore && limit < CONTACT_EMAIL_MAX_LIMIT;

  const loadMore = useCallback(async () => {
    if (!canLoadMore) return;
    setLoadingMore(true);
    try {
      await load(true, Math.min(limitRef.current + CONTACT_EMAIL_PAGE_SIZE, CONTACT_EMAIL_MAX_LIMIT));
    } finally {
      setLoadingMore(false);
    }
  }, [canLoadMore, load]);

  return { emails, status, loading, forbidden, error, hasMore, total, limit, canLoadMore, loadingMore, loadMore, reload, append };
}
